/**
 * Calendar dates in the SHOP's time zone, never the viewer's.
 *
 * A promotion or a credit ends at an instant. A date on screen is a promise
 * about a calendar day, and that day must be the same for everybody who reads
 * it: a Bucharest visitor of the Slovenian shop, a customer in New York, the
 * operator in Ljubljana. So it is read in the time zone of the market the
 * shop serves, not in the browser's.
 *
 * Pure, so the rule is tested (DST included) without a browser.
 */

export const SHOP_TIME_ZONE = "Europe/Ljubljana";

/** si, hr, sk, cz and at all run on Ljubljana time; only these differ. */
const TIME_ZONE_BY_MARKET: Record<string, string> = {
  pl: "Europe/Warsaw",
  ro: "Europe/Bucharest",
};

/** The market's time zone; Ljubljana for si/hr/sk/cz/at and anything unknown. */
export function marketTimeZone(market: string | null | undefined): string {
  return (market && TIME_ZONE_BY_MARKET[market.toLowerCase()]) || SHOP_TIME_ZONE;
}

const MARKET_BY_CURRENCY: Record<string, string> = {
  pln: "pl",
  ron: "ro",
};

/**
 * The time zone of the market a currency is sold in. EUR is sold in several
 * markets, all on Ljubljana time, so the answer is still unambiguous.
 */
export function currencyTimeZone(currency: string | null | undefined): string {
  return marketTimeZone(currency ? MARKET_BY_CURRENCY[currency.toLowerCase()] : null);
}

/**
 * The last calendar day on which something that ends at `iso` still applies:
 * the date of (end − 1 ms) in `timeZone`. An end at 7. 10. 00:00 is the end
 * of 6. 10., and that is the day a customer must be told.
 *
 * Returned as the instant to format (with the same `timeZone`), or null for a
 * missing or unreadable value.
 */
export function lastDayInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const end = Date.parse(iso);
  if (Number.isNaN(end)) return null;
  return new Date(end - 1);
}

/**
 * "6. 10." in sl, "06/10" in it: a short day + month, read in `timeZone`.
 *
 * `endsSentence`: the copy puts the date last and closes the sentence itself
 * ("Akcija velja do {{end}}."). sl, hr, sk, cs and de-AT close a short date
 * with a period of their own, so the banner read "do 6. 10.." — the date's
 * period is dropped and the sentence's stands for both.
 */
export function shortDayMonth(
  when: Date | null,
  locale: string,
  timeZone: string,
  opts: { endsSentence?: boolean } = {},
): string {
  if (!when || Number.isNaN(when.getTime())) return "";
  const label = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "numeric",
    timeZone,
  }).format(when);
  return opts.endsSentence ? label.replace(/\.\s*$/, "") : label;
}

/** The zone's offset from UTC, in minutes, at a given instant. */
function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/**
 * A `datetime-local` wall time ("2026-10-18T23:59") read in `timeZone`, as an
 * ISO instant. Independent of the runtime's own zone, so an admin abroad (or a
 * UTC build server) means the same moment as one in Ljubljana.
 */
export function zonedWallToIso(
  local: string,
  timeZone: string = SHOP_TIME_ZONE,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local || "");
  if (!m) return null;
  const [y, mo, d, h, mi, s] = m.slice(1).map((v) => Number(v ?? 0));
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  // Two passes settle the offset across a DST change.
  let guess = wallAsUtc - zoneOffsetMinutes(new Date(wallAsUtc), timeZone) * 60000;
  guess = wallAsUtc - zoneOffsetMinutes(new Date(guess), timeZone) * 60000;
  const out = new Date(guess);
  return Number.isFinite(out.getTime()) ? out.toISOString() : null;
}

/** An instant as a `datetime-local` wall time in `timeZone`. */
export function isoToZonedWall(
  value: string | Date,
  timeZone: string = SHOP_TIME_ZONE,
): string {
  const at = new Date(value);
  if (!Number.isFinite(at.getTime())) return "";
  const shifted = new Date(at.getTime() + zoneOffsetMinutes(at, timeZone) * 60000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${p(shifted.getUTCMonth() + 1)}-${p(shifted.getUTCDate())}T${p(shifted.getUTCHours())}:${p(shifted.getUTCMinutes())}`;
}
